import {
  classSet,
  Portal,
  useRect,
  useWindowSize
} from '@platform/ui-library';
import {
  CSSProperties,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  useLayoutEffect,
  useRef
} from 'react';
import { ClickOutside } from '@app/form_components';
import styles from './pop-up.module.scss';

export enum Direction {
  left = 'left',
  right = 'right'
}

const viewportPadding = 8;
const arrowInset = 12;
const verticalOffset = 18;

export type PopUpProps = HTMLAttributes<HTMLDivElement> & PropsWithChildren<{
  /** Direction to display the popup on. */
  direction?: Direction;
  handleClose: () => void;
  refRect?: ReturnType<typeof useRect>[1];
  useBackdrop?: boolean;
}>;

export const PopUp: FC<PopUpProps> = ({
  children,
  className,
  direction,
  handleClose,
  refRect,
  useBackdrop,
  ...props
}) => {
  const domElement = useRef<HTMLDivElement>(null);
  const [windowWidth, windowHeight] = useWindowSize();
  const anchorY = (refRect) ? refRect.top + refRect.height / 2 : 0;

  useLayoutEffect(() => {
    const el = domElement.current;
    if (!el || !refRect || !windowHeight) {
      return;
    }

    const popupRect = el.getBoundingClientRect();
    const maxBottom = windowHeight - viewportPadding;
    let nextTop = popupRect.top;

    if (popupRect.bottom > maxBottom) {
      nextTop -= popupRect.bottom - maxBottom;
      el.style.top = `${nextTop}px`;
    }

    const placedRect = el.getBoundingClientRect();
    const arrowOffset = Math.min(
      Math.max(anchorY - placedRect.top, arrowInset),
      placedRect.height - arrowInset
    );
    el.style.setProperty('--popup-arrow-offset', `${arrowOffset}px`);
  }, [
    anchorY,
    refRect,
    windowHeight,
    children
  ]);

  if (!refRect) {
    return null;
  }

  const resolvedDirection = direction ?? (
    (windowWidth && refRect.left >= windowWidth - refRect.right) ? (
      Direction.left
    ) : Direction.right
  );

  const style: CSSProperties = {
    ...props.style,
    top: `${anchorY}px`,
    '--popup-arrow-offset': `${verticalOffset}px`
  };

  if (resolvedDirection === Direction.left) {
    style.right = `${windowWidth - refRect.left}px`;
  } else {
    style.left = `${refRect.right}px`;
  }

  return (
    <Portal>
      {useBackdrop && <div className={styles.backdrop} onClick={handleClose} />}
      <ClickOutside onClickOutside={() => {
        if (!useBackdrop) {
          handleClose();
        }
      }}>
        <div
          className={classSet([
            styles.container,
            className,
            styles[resolvedDirection]
          ])}
          {...props}
          ref={domElement}
          style={style}
        >
          {children}
        </div>
      </ClickOutside>
    </Portal>
  );
};
